// Thin HTTP client for the CKN service APIs.
//
// Why not @ckn-technology/core's `Api`? It does not throw on non-2xx — it returns the error body
// as `data` with `ok: false` — so every call site would have to branch on `res.ok` and re-derive
// the error, and none of the retry/re-auth logic below could live in one place. This wraps `fetch`
// for all five verbs, resolves base URLs via apibase, attaches auth (auth.ts) + Idempotency-Key
// (idempotency.ts), unwraps the `{success,data,meta}` envelope, and throws a typed `AppError`.
//
// (This note used to also claim `Api` has no `patch()`. It does, as of 5.5.3 — verified against
// the installed `dist/net/api.d.ts`. The reason above is the one that still holds.)
import { serviceBase } from "../apibase.js";
import { currentCredential } from "./auth.js";
import { rtOrigin } from "./realtime.js";
import { newIdempotencyKey } from "./idempotency.js";
import { surfaceToken } from "./surface.js";
import { AppError } from "./errors.js";
import { handleAuthFailure, noteAuthSuccess } from "./reauth.js";
import { renewNow } from "./keepalive.js";
const DEFAULT_TIMEOUT_MS = 30_000;
/**
 * Gateway failures — the proxy could not reach the service — as opposed to the service itself
 * refusing the call. Every ckn.business service sits behind Apache, which answers its own
 * "503 Service Unavailable" HTML page for as long as the Node process behind it is away. The
 * hole is seconds wide (a restart), but it lands on whoever happened to load a screen inside it,
 * and it is not rare: 997 of them reached the Hub in 90 days, every one on attempt 1, because
 * `integration.flow_steps.retry_max_attempts` is NULL on all 6,624 service steps and the retry
 * configured on the ENDPOINT (ORDERS.RECEIPT.LIST: 3 attempts / 500ms) is never what runs.
 * Until that is fixed Hub-side, the browser is the only layer that can turn a two-second gap
 * back into a slow load rather than a dead pane.
 */
const TRANSIENT_STATUS = new Set([502, 503, 504]);
/** The original call plus two re-runs — 1.6s of patience, which covers an ordinary restart
 *  without making a service that is genuinely down feel like a hang. */
const TRANSIENT_MAX_ATTEMPTS = 3;
const TRANSIENT_BACKOFF_MS = [400, 1200];
/**
 * The write verbs a transient failure may re-run — and ONLY when the call carries an
 * Idempotency-Key (see `retriableWrite` in request()). A write that 502/503s, or whose socket
 * dropped, may or may not have reached the service; nothing here can tell. The key is what makes
 * the re-run safe: the service answers a repeated key with its FIRST result, so a re-run of a
 * write that did land is a no-op and a re-run of one that did not is the write itself. Without
 * the key a replay is how a sale gets recorded twice, which is why a keyless write still gets
 * exactly one attempt (A7, 2026-09-04 — three orders created in a burst, one lost to a gateway
 * hole and never retried).
 */
const RETRIABLE_WRITE_METHODS = new Set(["POST", "PUT"]);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
function buildQuery(query) {
    if (!query)
        return "";
    const parts = [];
    for (const [k, v] of Object.entries(query)) {
        if (v === undefined || v === null || v === "")
            continue;
        parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
    }
    return parts.length ? `?${parts.join("&")}` : "";
}
export async function request(method, domain, path, opts = {}) {
    const p = path.startsWith("/") ? path : `/${path}`;
    const url = `${serviceBase(domain)}${p}${buildQuery(opts.query)}`;
    const headers = new Headers({ "Content-Type": "application/json", Accept: "application/json" });
    for (const [k, v] of Object.entries(currentCredential()))
        headers.set(k, v);
    // Held in a local so the 401 retry below can send the SAME key. A regenerated one would make
    // the re-run a second write as far as the service is concerned, which is the exact thing an
    // idempotency key exists to prevent.
    const idempotencyKey = opts.idempotent ? (opts.idempotencyKey ?? newIdempotencyKey()) : undefined;
    if (idempotencyKey)
        headers.set("Idempotency-Key", idempotencyKey);
    // A write this client is allowed to re-run on a transient failure: the verb is one of the
    // two that carry a key in this app, AND the key is actually on the request. Never true for a
    // keyless write — see RETRIABLE_WRITE_METHODS. GET keeps its own rule below, unchanged.
    const retriableWrite = RETRIABLE_WRITE_METHODS.has(method) && !!idempotencyKey;
    // Which transient-failure attempt this is (1 = the original call) — shared by the network
    // and the gateway-status re-runs below, so a call cannot get both budgets.
    const attempt = opts.attempt ?? 1;
    // Caller surface (audit): a granular call that the /api proxy routes through the Hub is recorded
    // in the execution's context.__caller, so even per-endpoint runs show WHICH app/screen made them.
    try {
        const path = globalThis.location?.pathname;
        if (path)
            headers.set("x-ckn-source", "brandos:" + String(path));
    }
    catch { /* no location (SSR/test) → run stays unattributed */ }
    // This tab's realtime id: the /api proxy stamps it on the change frames a write triggers
    // (WS /rt), so THIS tab's own writes don't bounce back as a refetch signal. On every verb —
    // the server ignores it on reads, and branching here would save nothing.
    headers.set("x-bo-rt", rtOrigin);
    // The storefront's surface credential, for browsers that keep no cookies (see api/surface.ts).
    // "" on every other surface and whenever the fetch failed — the cookie still carries those.
    const surface = await surfaceToken();
    if (surface)
        headers.set("x-bo-surface", surface);
    // no-store: a GET right after a write (e.g. reopening an invoice just marked paid) must see
    // the fresh row, not a browser-cached one from moments earlier at the same URL.
    const init = { method, headers, cache: "no-store" };
    if (opts.body !== undefined && method !== "GET")
        init.body = JSON.stringify(opts.body);
    // Timeout via AbortController (respects a caller-supplied signal too).
    const controller = new AbortController();
    const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const timer = timeoutMs ? setTimeout(() => controller.abort(), timeoutMs) : null;
    if (opts.signal)
        opts.signal.addEventListener("abort", () => controller.abort(), { once: true });
    init.signal = controller.signal;
    let res;
    try {
        res = await fetch(url, init);
    }
    catch (e) {
        const aborted = e?.name === "AbortError";
        // THE SOCKET DROPPED before an answer came back (DNS, reset, the proxy going away mid
        // request). For an idempotent write that is the same hole as a 503 — the service may or
        // may not have seen it, and the key makes re-asking safe — so it gets the same bounded
        // re-run. Not on an abort: a timeout has already spent 30s, and a caller that abandoned
        // the call is not waiting for it. Not on GET either — a read's rule is unchanged (below).
        if (retriableWrite && !aborted && attempt < TRANSIENT_MAX_ATTEMPTS && !opts.signal?.aborted) {
            if (timer)
                clearTimeout(timer);
            await sleep(TRANSIENT_BACKOFF_MS[attempt - 1] ?? 1200);
            return request(method, domain, path, { ...opts, attempt: attempt + 1, idempotencyKey });
        }
        const msg = aborted ? "Request timed out" : (e?.message ?? "Network error");
        throw new AppError("NETWORK_ERROR", msg, 0);
    }
    finally {
        if (timer)
            clearTimeout(timer);
    }
    const text = await res.text();
    let envelope = null;
    if (text) {
        try {
            envelope = JSON.parse(text);
        }
        catch {
            envelope = null;
        }
    }
    if (!res.ok || envelope?.success === false || envelope?.error) {
        // 401 = the access token behind this screen has lapsed. keepalive.ts renews on a ten-minute
        // clock, so getting here means the clock lost — a machine that slept through its alarm, a
        // renewal that could not reach the IdP, a tab restored from the back/forward cache. Renew
        // once and re-run the call: the operator sees a request that took a moment, not a screen
        // that threw them out mid-sale. Only when the renewal genuinely succeeds, and only once.
        if (res.status === 401 && !opts.retried && (await renewNow()) === "ok") {
            return request(method, domain, path, { ...opts, retried: true, idempotencyKey });
        }
        // GATEWAY HOLE, not a refusal — re-run it rather than painting the failure on the pane.
        // A GET can be repeated by definition. A write that 503s may or may not have reached the
        // service (Apache answers before the body is forwarded, but nothing here can prove that),
        // and replaying one on a guess is how a sale gets recorded twice — so a write is re-run
        // ONLY when it carries an Idempotency-Key (`retriableWrite`), and the re-run sends the
        // SAME key, so the service answers the first result rather than booking a second order.
        // A keyless write still gets one attempt and its failure, as before.
        //
        // Also skipped once the caller has abandoned the call: a pane that unmounted mid-request
        // aborts its signal, and waking up 1.2s later to re-fetch for it is work nobody is
        // waiting on.
        if ((method === "GET" || retriableWrite) && TRANSIENT_STATUS.has(res.status) && attempt < TRANSIENT_MAX_ATTEMPTS && !opts.signal?.aborted) {
            await sleep(TRANSIENT_BACKOFF_MS[attempt - 1] ?? 1200);
            return request(method, domain, path, { ...opts, attempt: attempt + 1, idempotencyKey });
        }
        // The session is really gone (or this surface has none): start the re-auth navigation,
        // then still throw so the caller unwinds normally — the page is on its way out either way.
        handleAuthFailure(res.status);
        const err = envelope?.error;
        // Suppress rule (multi-client §5.3): the tenant's own config switched this off — panes
        // read isFeatureDisabled(e) and render the disabled state, not a failure.
        const message = err?.code === "FLOW_SUPPRESSED_FOR_TENANT"
            ? "This feature is not enabled for your organisation."
            : err?.message ?? envelope?.message ?? res.statusText ?? "Request failed";
        throw new AppError(err?.code ?? `HTTP_${res.status}`, message, res.status, err?.details);
    }
    noteAuthSuccess();
    // Success. `data` is the envelope payload; some endpoints (rare) return a bare value.
    const data = (envelope && "data" in envelope ? envelope.data : envelope);
    return { data, meta: envelope?.meta, status: res.status };
}
export const http = {
    get: (domain, path, opts) => request("GET", domain, path, opts),
    post: (domain, path, opts) => request("POST", domain, path, opts),
    patch: (domain, path, opts) => request("PATCH", domain, path, opts),
    put: (domain, path, opts) => request("PUT", domain, path, opts),
    del: (domain, path, opts) => request("DELETE", domain, path, opts),
};
//# sourceMappingURL=client.js.map